import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";

export type ConfirmOptions = {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  /** Libellé du bouton qui renonce (« Annuler » par défaut). */
  cancelLabel?: string;
  /** Action destructrice : bouton rouge, et le focus va d'abord sur « Annuler ». */
  danger?: boolean;
};

export type PromptOptions = ConfirmOptions & {
  label: string;
  placeholder?: string;
  initial?: string;
  /** Réponse obligatoire (non vide). */
  required?: boolean;
  multiline?: boolean;
  /** Message d'erreur si la réponse ne convient pas, sinon null. */
  validate?: (value: string) => string | null;
};

type Request =
  | { id: number; kind: "confirm"; options: ConfirmOptions; resolve: (ok: boolean) => void }
  | { id: number; kind: "prompt"; options: PromptOptions; resolve: (value: string | null) => void };

type Dialogs = {
  /** Vrai si l'utilisateur confirme. */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** La réponse (sans espaces autour), ou null si l'utilisateur annule. */
  prompt: (options: PromptOptions) => Promise<string | null>;
};

const DialogContext = createContext<Dialogs | null>(null);

/** Dialogues de l'UI : remplacent confirm/prompt/alert du navigateur, accessibles au clavier et aux lecteurs d'écran (PB-73). */
export function useDialog(): Dialogs {
  const dialogs = useContext(DialogContext);
  if (!dialogs) throw new Error("useDialog must be used inside <DialogProvider>.");
  return dialogs;
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);
  const sequence = useRef(0);
  const confirm = useCallback(
    (options: ConfirmOptions) => new Promise<boolean>((resolve) => setRequest({ id: ++sequence.current, kind: "confirm", options, resolve })),
    [],
  );
  const prompt = useCallback(
    (options: PromptOptions) => new Promise<string | null>((resolve) => setRequest({ id: ++sequence.current, kind: "prompt", options, resolve })),
    [],
  );
  const [value] = useState<Dialogs>(() => ({ confirm, prompt }));

  return (
    <DialogContext.Provider value={value}>
      {children}
      {request && <Modal key={request.id} request={request} onClose={() => setRequest(null)} />}
    </DialogContext.Provider>
  );
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

function Modal({ request, onClose }: { request: Request; onClose: () => void }) {
  const { options } = request;
  const prompt = request.kind === "prompt" ? request.options : null;
  const [text, setText] = useState(prompt?.initial ?? "");
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const confirmButton = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const messageId = useId();
  const errorId = useId();

  // Focus : le champ, sinon « Annuler » pour une action destructrice, sinon « Confirmer » ; rendu à l'élément d'origine à la fermeture.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    (field.current ?? (options.danger ? cancelButton.current : confirmButton.current))?.focus();
    return () => previous?.focus?.();
  }, [options.danger]);

  const finish = (result: boolean) => {
    if (request.kind === "confirm") {
      request.resolve(result);
      onClose();
      return;
    }
    if (!result) {
      request.resolve(null);
      onClose();
      return;
    }
    const answer = text.trim();
    const problem = prompt?.required && !answer ? "Une réponse est nécessaire." : (prompt?.validate?.(answer) ?? null);
    if (problem) {
      setError(problem);
      field.current?.focus();
      return;
    }
    request.resolve(answer);
    onClose();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      finish(false);
      return;
    }
    if (e.key !== "Tab" || !box.current) return;
    // Le focus reste dans la fenêtre (aria-modal) : Tab boucle du dernier au premier élément, Maj+Tab l'inverse.
    const items = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    finish(true);
  };

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && finish(false)}>
      <div
        ref={box}
        className="modal card"
        role={options.danger ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby={options.title ? titleId : undefined}
        aria-label={options.title ? undefined : typeof options.message === "string" ? options.message : "Confirmation"}
        aria-describedby={messageId}
        onKeyDown={onKeyDown}
      >
        <form onSubmit={submit} className="stack" style={{ gap: 12 }}>
          {options.title && <h2 id={titleId} style={{ margin: 0 }}>{options.title}</h2>}
          <div id={messageId}>{options.message}</div>
          {prompt && (
            <label className="small">
              {prompt.label}
              {prompt.multiline ? (
                <textarea
                  ref={field}
                  rows={3}
                  value={text}
                  placeholder={prompt.placeholder}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? errorId : undefined}
                  onChange={(e) => setText(e.target.value)}
                />
              ) : (
                <input
                  ref={field}
                  value={text}
                  placeholder={prompt.placeholder}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? errorId : undefined}
                  onChange={(e) => setText(e.target.value)}
                />
              )}
            </label>
          )}
          {error && (
            <div id={errorId} role="alert" className="small" style={{ color: "var(--danger)" }}>
              {error}
            </div>
          )}
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button ref={cancelButton} type="button" className="ghost small" onClick={() => finish(false)}>
              {options.cancelLabel ?? "Annuler"}
            </button>
            <button ref={confirmButton} type="submit" className={`small ${options.danger ? "danger" : "primary"}`}>
              {options.confirmLabel ?? "Confirmer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
